/**
 * MyGrades — Iter. 411.dd
 * ───────────────────────
 * Full identity-progression tree for the user's active profile.
 *
 * Users land here from the "Profil <Grade>" pill in the Dashboard header
 * (or `/mes-grades`). Purpose: give a client a clear line-of-sight from
 * their current grade all the way to Legende, with concrete "X days /
 * Y pts left" gates on each locked step. This turns the abstract loyalty
 * feeling into a visible ladder → higher retention past the 90-day mark.
 *
 * Backend: single call to /gamification/profile-grade/current. Response
 * carries `all_grades` (5 items) with `unlocked / is_current / *_remaining`.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { api, useLanguage } from '../context/AppContext';
import { ArrowLeft, Lock, Sparkles, Loader2, TrendingUp, CalendarDays } from 'lucide-react';

export default function MyGrades() {
  const { language } = useLanguage();
  const fr = language === 'fr';
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/gamification/profile-grade/current')
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, []);

  if (error) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto p-4 text-center text-white/60">
          {fr ? 'Impossible de charger tes grades pour le moment.' : 'Unable to load your grades right now.'}
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto p-4 flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  const grades = data.all_grades || [];
  const stats = data.stats || { active_days: 0, total_glory: 0 };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto p-4 pb-24" data-testid="my-grades-page">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-full flex items-center justify-center transition-all active:scale-95"
            style={{
              background: 'linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(15,15,15,0.68) 45%, rgba(251,146,60,0.14) 100%)',
              border: '1px solid rgba(251,146,60,0.35)',
              boxShadow: '0 0 10px rgba(251,146,60,0.25)',
              backdropFilter: 'blur(6px)',
            }}
            data-testid="my-grades-back-btn"
          >
            <ArrowLeft className="w-4 h-4" style={{ color: '#fed7aa', filter: 'drop-shadow(0 0 4px rgba(251,146,60,0.7))' }} />
          </button>
          <div className="flex-1 min-w-0">
            <h1
              className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight leading-none"
              style={{ color: '#fed7aa', textShadow: '0 0 10px rgba(251,146,60,0.6)' }}
              data-testid="my-grades-title"
            >
              {fr ? 'Mes Grades' : 'My Grades'}
            </h1>
            <p className="text-xs text-white/60 mt-1">
              {fr
                ? `${stats.active_days} jours actifs · ${stats.total_glory} pts totaux`
                : `${stats.active_days} active days · ${stats.total_glory} total pts`}
            </p>
          </div>
        </div>

        {/* Explainer */}
        <div
          className="mb-6 rounded-xl p-3.5"
          style={{
            background: 'linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(15,15,15,0.68) 45%, rgba(251,146,60,0.16) 100%)',
            border: '1px solid rgba(251,146,60,0.4)',
            boxShadow: '0 0 12px rgba(251,146,60,0.3)',
            backdropFilter: 'blur(6px)',
          }}
        >
          <div className="flex items-start gap-2">
            <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#fed7aa', filter: 'drop-shadow(0 0 5px rgba(251,146,60,0.85))' }} />
            <p className="text-xs text-white/85 leading-relaxed">
              {fr
                ? "Ton identité évolue avec ta constance. Chaque grade demande à la fois du temps actif ET des points gagnés — pas de raccourci possible."
                : 'Your identity evolves with your consistency. Each grade requires both active time AND earned points — no shortcuts possible.'}
            </p>
          </div>
        </div>

        {/* Grade tree */}
        <div className="space-y-3">
          {grades.map((g, i) => {
            const rgb = g.color_rgb;
            const isLast = i === grades.length - 1;
            const label = fr ? g.label_fr : g.label_en;
            const stateColor = g.unlocked ? rgb : '107,114,128';    // slate-500 if locked
            const cardOpacity = g.unlocked ? 1 : 0.55;
            const daysLeft = g.days_remaining;
            const gloryLeft = g.glory_remaining;

            return (
              <div key={g.idx} className="relative" data-testid={`grade-row-${g.idx}`}>
                {/* Vertical connector line to next tile */}
                {!isLast && (
                  <span
                    aria-hidden="true"
                    className="absolute left-8 top-16 bottom-[-12px] w-0.5"
                    style={{
                      background: g.unlocked
                        ? `linear-gradient(to bottom, rgba(${rgb},0.7), rgba(${grades[i+1].color_rgb},0.35))`
                        : 'linear-gradient(to bottom, rgba(107,114,128,0.4), rgba(107,114,128,0.15))',
                    }}
                  />
                )}
                <div
                  className="flex items-center gap-3 p-3 rounded-xl relative"
                  style={{
                    opacity: cardOpacity,
                    background: g.is_current
                      ? `linear-gradient(135deg, rgba(0,0,0,0.75) 0%, rgba(15,15,15,0.7) 40%, rgba(${rgb},0.28) 100%)`
                      : g.unlocked
                        ? `linear-gradient(135deg, rgba(0,0,0,0.72) 0%, rgba(15,15,15,0.68) 45%, rgba(${rgb},0.18) 100%)`
                        : 'linear-gradient(135deg, rgba(0,0,0,0.6) 0%, rgba(15,15,15,0.55) 100%)',
                    border: g.is_current
                      ? `1.5px solid rgba(${rgb},0.75)`
                      : g.unlocked
                        ? `1px solid rgba(${rgb},0.45)`
                        : '1px solid rgba(107,114,128,0.3)',
                    boxShadow: g.is_current
                      ? `0 0 22px rgba(${rgb},${0.35 + (g.intensity || 1) * 0.05}), 0 0 6px rgba(${rgb},0.35), inset 0 1px 0 rgba(255,255,255,0.06)`
                      : g.unlocked
                        ? `0 0 12px rgba(${rgb},${0.25 + (g.intensity || 1) * 0.03}), inset 0 1px 0 rgba(255,255,255,0.05)`
                        : 'inset 0 1px 0 rgba(255,255,255,0.03)',
                    backdropFilter: 'blur(6px)',
                  }}
                >
                  {/* Emblem */}
                  <div
                    className="shrink-0 w-14 h-14 rounded-full flex items-center justify-center relative z-10"
                    style={{
                      background: g.unlocked
                        ? `linear-gradient(140deg, rgba(${rgb},0.55), rgba(${rgb},0.2))`
                        : 'linear-gradient(140deg, rgba(107,114,128,0.35), rgba(107,114,128,0.12))',
                      boxShadow: g.unlocked
                        ? `0 0 16px rgba(${rgb},${0.5 + (g.intensity || 1) * 0.06}), inset 0 0 0 2px rgba(${rgb},0.55)`
                        : 'inset 0 0 0 1px rgba(107,114,128,0.4)',
                    }}
                  >
                    {g.unlocked ? (
                      <span
                        className="text-3xl leading-none"
                        style={{ filter: `drop-shadow(0 0 8px rgba(${rgb},0.9))` }}
                        aria-hidden="true"
                      >
                        {g.emoji}
                      </span>
                    ) : (
                      <Lock className="w-6 h-6 text-white/50" />
                    )}
                  </div>

                  {/* Label + threshold */}
                  <div className="flex-1 min-w-0">
                    <p
                      className="font-heading text-sm sm:text-base font-black uppercase tracking-widest leading-tight"
                      style={{
                        color: g.unlocked ? `rgba(${rgb},1)` : 'rgba(148,163,184,0.85)',
                        textShadow: g.unlocked ? `0 0 6px rgba(${rgb},0.55)` : 'none',
                      }}
                    >
                      {label}
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px]">
                      <span
                        className="inline-flex items-center gap-1"
                        style={{ color: `rgba(${stateColor},${g.unlocked ? 0.85 : 0.65})` }}
                      >
                        <CalendarDays className="w-3 h-3" />
                        {g.min_days === 0 ? (fr ? 'Départ' : 'Start') : `${g.min_days}j`}
                        {!g.unlocked && daysLeft > 0 && (
                          <span className="text-white/50">({fr ? `+${daysLeft}j` : `+${daysLeft}d`})</span>
                        )}
                      </span>
                      <span
                        className="inline-flex items-center gap-1"
                        style={{ color: `rgba(${stateColor},${g.unlocked ? 0.85 : 0.65})` }}
                      >
                        <TrendingUp className="w-3 h-3" />
                        {g.min_glory === 0 ? '—' : `${g.min_glory} pts`}
                        {!g.unlocked && gloryLeft > 0 && (
                          <span className="text-white/50">(+{gloryLeft})</span>
                        )}
                      </span>
                    </div>
                  </div>

                  {/* Current-grade pin */}
                  {g.is_current && (
                    <span
                      className="absolute -top-2 right-3 text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full"
                      style={{
                        background: `linear-gradient(140deg, rgba(${rgb},0.9), rgba(${rgb},0.6))`,
                        color: '#fff',
                        boxShadow: `0 0 10px rgba(${rgb},0.7)`,
                        textShadow: '0 0 6px rgba(0,0,0,0.4)',
                      }}
                    >
                      {fr ? 'Actuel' : 'Current'}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer nudge */}
        <div className="mt-8 text-center text-xs text-white/50 italic">
          {fr
            ? "La Légende n'est pas un titre — c'est un chemin."
            : 'Legend is not a title — it is a path.'}
        </div>
      </div>
    </Layout>
  );
}
